import { Tweet } from './tweetService';

export interface BuzzAnalysis {
  topTweets: Array<{
    text: string;
    engagement: number;
    likes: number;
    retweets: number;
    engagementRate: number;
  }>;
  patterns: {
    hasQuestion: number;
    hasNumbers: number;
    hasEmoji: number;
    hasHashtag: number;
    hasLink: number;
    hasLineBreak: number;
    avgLength: number;
    timePatterns: Record<string, number>;
    dayPatterns: Record<string, number>;
  };
  contentAnalysis: {
    topKeywords: Array<{ word: string; count: number }>;
    topHashtags: Array<{ tag: string; count: number }>;
    toneDistribution: Record<string, number>;
  };
}

export const archiveAnalyzer = {
  /**
   * Run detailed buzz analysis from Twitter Archive
   */
  analyzeBuzzPatterns(tweets: Tweet[]): BuzzAnalysis {
    const tweetsWithEngagement = tweets
      .map(t => ({
        ...t,
        engagement: (t.likes || 0) + (t.retweets || 0) * 2,
        engagementRate: this.calculateEngagementRate(t)
      }))
      .filter(t => t.engagement > 0)
      .sort((a, b) => b.engagement - a.engagement);

    const top20Count = Math.max(5, Math.floor(tweetsWithEngagement.length * 0.2));
    const topTweets = tweetsWithEngagement.slice(0, top20Count).map(t => ({
      text: t.text,
      engagement: t.engagement,
      likes: t.likes,
      retweets: t.retweets,
      engagementRate: t.engagementRate
    }));

    const patterns = this.analyzePatterns(topTweets.map(t => t.text));

    const timePatterns = this.analyzeTimePatterns(tweetsWithEngagement);
    const dayPatterns = this.analyzeDayPatterns(tweetsWithEngagement);

    patterns.timePatterns = timePatterns;
    patterns.dayPatterns = dayPatterns;

    const contentAnalysis = this.analyzeContent(topTweets.map(t => t.text));

    return {
      topTweets,
      patterns,
      contentAnalysis
    };
  },

  calculateEngagementRate(tweet: Tweet): number {
    const totalEngagement = (tweet.likes || 0) + (tweet.retweets || 0) + (tweet.replies || 0);
    return totalEngagement;
  },

  analyzePatterns(texts: string[]): BuzzAnalysis['patterns'] {
    let hasQuestionCount = 0;
    let hasNumbersCount = 0;
    let hasEmojiCount = 0;
    let hasHashtagCount = 0;
    let hasLinkCount = 0;
    let hasLineBreakCount = 0;
    let totalLength = 0;

    texts.forEach(text => {
      if (text.includes('？') || text.includes('?')) hasQuestionCount++;
      if (/\d+/.test(text)) hasNumbersCount++;
      if (/[\u{1F300}-\u{1F9FF}]/u.test(text)) hasEmojiCount++;
      if (text.includes('#')) hasHashtagCount++;
      if (/https?:\/\//.test(text)) hasLinkCount++;
      if (text.includes('\n')) hasLineBreakCount++;
      totalLength += text.length;
    });

    const count = texts.length || 1;

    return {
      hasQuestion: hasQuestionCount / count,
      hasNumbers: hasNumbersCount / count,
      hasEmoji: hasEmojiCount / count,
      hasHashtag: hasHashtagCount / count,
      hasLink: hasLinkCount / count,
      hasLineBreak: hasLineBreakCount / count,
      avgLength: totalLength / count,
      timePatterns: {},
      dayPatterns: {}
    };
  },

  analyzeTimePatterns(tweets: Array<Tweet & { engagement: number }>): Record<string, number> {
    const hourlyEngagement: Record<string, { total: number; count: number }> = {};

    tweets.forEach(t => {
      try {
        const date = new Date(t.createdAt);
        const hour = date.getHours();
        const hourRange = this.getHourRange(hour);

        if (!hourlyEngagement[hourRange]) {
          hourlyEngagement[hourRange] = { total: 0, count: 0 };
        }
        hourlyEngagement[hourRange].total += t.engagement;
        hourlyEngagement[hourRange].count++;
      } catch (e) {
        // ignore date parse errors
      }
    });

    const result: Record<string, number> = {};
    Object.entries(hourlyEngagement).forEach(([range, data]) => {
      result[range] = data.count > 0 ? data.total / data.count : 0;
    });

    return result;
  },

  analyzeDayPatterns(tweets: Array<Tweet & { engagement: number }>): Record<string, number> {
    const dayEngagement: Record<string, { total: number; count: number }> = {};
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    tweets.forEach(t => {
      try {
        const date = new Date(t.createdAt);
        const dayName = days[date.getDay()];

        if (!dayEngagement[dayName]) {
          dayEngagement[dayName] = { total: 0, count: 0 };
        }
        dayEngagement[dayName].total += t.engagement;
        dayEngagement[dayName].count++;
      } catch (e) {
        // ignore date parse errors
      }
    });

    const result: Record<string, number> = {};
    Object.entries(dayEngagement).forEach(([day, data]) => {
      result[day] = data.count > 0 ? data.total / data.count : 0;
    });

    return result;
  },

  getHourRange(hour: number): string {
    if (hour >= 6 && hour < 9) return '6-9 AM';
    if (hour >= 9 && hour < 12) return '9-12 AM';
    if (hour >= 12 && hour < 15) return '12-3 PM';
    if (hour >= 15 && hour < 18) return '3-6 PM';
    if (hour >= 18 && hour < 21) return '6-9 PM';
    if (hour >= 21 && hour < 24) return '9-12 AM';
    return '12-6 AM';
  },

  analyzeContent(texts: string[]): BuzzAnalysis['contentAnalysis'] {
    const wordCount = new Map<string, number>();
    const hashtagCount = new Map<string, number>();

    const stopWords = new Set([
      'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
      'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
      'should', 'may', 'might', 'can', 'shall', 'to', 'of', 'in', 'for',
      'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during',
      'before', 'after', 'above', 'below', 'between', 'out', 'off', 'over',
      'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when',
      'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few', 'more',
      'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own',
      'same', 'so', 'than', 'too', 'very', 'just', 'because', 'but', 'and',
      'or', 'if', 'while', 'about', 'up', 'its', 'it', 'this', 'that',
      'these', 'those', 'i', 'me', 'my', 'we', 'our', 'you', 'your',
      'he', 'him', 'his', 'she', 'her', 'they', 'them', 'their', 'what',
      'which', 'who', 'whom', 'get', 'got', 'like', 'dont', "don't",
    ]);

    texts.forEach(text => {
      // Extract hashtags
      const hashtags = text.match(/#[^\s#]+/g) || [];
      hashtags.forEach(tag => {
        hashtagCount.set(tag, (hashtagCount.get(tag) || 0) + 1);
      });

      // Extract keywords (English words, 3+ chars, excluding stop words)
      const words = text.toLowerCase().match(/[a-z]{3,}/g) || [];
      words.forEach(word => {
        if (!stopWords.has(word)) {
          wordCount.set(word, (wordCount.get(word) || 0) + 1);
        }
      });
    });

    const topKeywords = Array.from(wordCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word, count]) => ({ word, count }));

    const topHashtags = Array.from(hashtagCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag, count]) => ({ tag, count }));

    const toneDistribution = this.analyzeToneDistribution(texts);

    return {
      topKeywords,
      topHashtags,
      toneDistribution
    };
  },

  analyzeToneDistribution(texts: string[]): Record<string, number> {
    let positiveCount = 0;
    let negativeCount = 0;
    let neutralCount = 0;

    const positiveWords = ['amazing', 'awesome', 'great', 'love', 'best', 'good', 'nice', 'thanks', 'grateful', 'happy', 'excited', 'incredible', 'fantastic', 'beautiful', 'perfect'];
    const negativeWords = ['sad', 'terrible', 'worst', 'hate', 'bad', 'awful', 'tired', 'frustrated', 'scared', 'painful', 'annoying', 'broken', 'failed', 'stuck', 'struggling'];

    texts.forEach(text => {
      const lower = text.toLowerCase();
      let hasPositive = false;
      let hasNegative = false;

      positiveWords.forEach(word => {
        if (lower.includes(word)) hasPositive = true;
      });
      negativeWords.forEach(word => {
        if (lower.includes(word)) hasNegative = true;
      });

      if (hasPositive && !hasNegative) positiveCount++;
      else if (hasNegative && !hasPositive) negativeCount++;
      else neutralCount++;
    });

    const total = texts.length || 1;

    return {
      'Positive': positiveCount / total,
      'Negative': negativeCount / total,
      'Neutral': neutralCount / total
    };
  },

  getBestPostingTime(analysis: BuzzAnalysis): {
    bestHour: string;
    bestDay: string;
    confidence: number;
  } {
    const { timePatterns, dayPatterns } = analysis.patterns;

    let bestHour = '12-3 PM';
    let maxHourEngagement = 0;
    Object.entries(timePatterns).forEach(([hour, engagement]) => {
      if (engagement > maxHourEngagement) {
        maxHourEngagement = engagement;
        bestHour = hour;
      }
    });

    let bestDay = 'Fri';
    let maxDayEngagement = 0;
    Object.entries(dayPatterns).forEach(([day, engagement]) => {
      if (engagement > maxDayEngagement) {
        maxDayEngagement = engagement;
        bestDay = day;
      }
    });

    const dataCount = analysis.topTweets.length;
    const confidence = Math.min(100, (dataCount / 20) * 100);

    return {
      bestHour,
      bestDay,
      confidence
    };
  }
};
